<template>
  <div
    class="d-flex flex-column align-start gap-1"
    style="min-width: 50px"
    v-intersect.once="{
      handler: onIntersect,
      options: {
        threshold: [0, 0.5, 1.0]
      }
    }">
    <span v-if="canShowAssetInfo && showAsset" class="d-flex flex-row gap-1 align-center">
      <span
        class="typography-body-sm-bold text-capitalize"
        data-testid="yard-asset-presenter-asset-code">
        {{ assetCode }}
      </span>
      <v-chip
        variant="tonal"
        size="x-small"
        class="typography-chips-sm text-capitalize"
        data-testid="yard-asset-presenter-asset-type">
        {{ assetType }}
      </v-chip>
    </span>

    <span v-if="showSpot" class="d-flex flex-row gap-1 align-center">
      <v-icon :icon="getIconByAssetStatus()" :size="getIconSize()" />
      <v-skeleton-loader v-if="isLoading" type="text" style="min-width: 50px" />
      <yard-spot-presenter
        v-else-if="hasAssignment"
        :spot-id="spotId"
        :size="size"
        style="min-width: 50px" />
      <template v-else>
        <span
          class="text-muted"
          data-testid="yard-asset-presenter-waiting-assignment"
          :class="size === 'small' ? 'typography-body-sm' : 'typography-body-md'">
          {{ t('yard.drawers.createTaskDrawer.labels.waitingAssignment') }}
        </span>
      </template>
    </span>
  </div>
</template>

<script>
import { defineComponent, computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import YardSpotPresenter from './YardSpotPresenter.vue';
import { useYardAssetPresenter } from './useYardAssetPresenter';

export default defineComponent({
  name: 'YardAssetPresenter',
  components: {
    YardSpotPresenter
  },
  props: {
    size: {
      type: String,
      required: false,
      default: 'regular',
      validator(value) {
        return ['regular', 'small'].includes(value);
      }
    },
    variant: {
      type: String,
      required: false,
      default: 'default',
      validator(value) {
        return ['default', 'asset-only', 'spot-only'].includes(value);
      }
    },
    asset: {
      type: Object,
      required: false,
      default: null
    },
    assetId: {
      type: String,
      required: false,
      default: null
    }
  },
  setup(props) {
    const { t } = useI18n();

    const { assetId, asset } = toRefs(props);

    const { assetCode, assetType, spotId, isLoading, refresh } = useYardAssetPresenter(
      { assetId, asset },
      true
    );

    const canShowAssetInfo = computed(() => {
      return assetCode.value != null && assetType.value != null;
    });

    const hasAssignment = computed(() => {
      return spotId.value != null;
    });

    const getIconByAssetStatus = () => {
      if (hasAssignment.value) {
        return 'mdi-map-marker-outline';
      }
      return 'mdi-clock-outline';
    };

    const getIconSize = () => {
      if (props.size === 'small') {
        return '12';
      }
      return '16';
    };

    const showAsset = computed(() => {
      return ['default', 'asset-only'].includes(props.variant);
    });

    const showSpot = computed(() => {
      return ['default', 'spot-only'].includes(props.variant);
    });

    const onIntersect = () => {
      refresh();
    };

    return {
      assetCode,
      assetType,
      isLoading,
      hasAssignment,
      spotId,
      canShowAssetInfo,
      showAsset,
      showSpot,

      t,
      getIconByAssetStatus,
      getIconSize,
      onIntersect
    };
  }
});
</script>

<style scoped>
:deep(.v-skeleton-loader__text) {
  /** this value makes the loader have the same vertical spacing as the asset code text */
  margin: 6px 0 !important;
}
</style>
